import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import CustomToolbar from "../CustomToolbar";
import CustomColumnMenu from "../CustomColumnMenu";
import { toast } from "sonner";
import { Box } from "@mui/material";
import LockHeader from "./LockHeader";

export default function DashboardGrid({
  rows,
  columns,
  selectionModel,
  setSelectionModel,
  handleEdit,
  handleAddRow,
  handleExport,
  ownerFilter,
  setOwnerFilter,
  ownerList,
  rowsData,
  openFilter,
  handleImport,
  downloadSampleExcel,
  openOptionEditor,
  setCarrierDialog,
  setCreditDialog,
  debouncedSearch,
  handleDelete,
  user,
  isAdmin,
  setClaimDialog,
  setFilterModel,
  setSortField,
  setSortOrder,
  apiRef,
  filterModel,
  isSuperAdmin,
  setColumnDialog,
  columnRestrictions,
  setColumnRestrictions,
  openPermissionDialog,
}) {
  const canEditColumn = (field) => {
    if (isAdmin) return true;

    const allowedUsers = columnRestrictions?.[field] || [];

    if (!allowedUsers?.length) {
      return true;
    }

    return allowedUsers.includes(user?.username);
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
      }}
    >
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        filterModel={filterModel}
        rowBuffer={30}
        columns={columns}
        getRowId={(row) => row.id}
        getRowHeight={() => "auto"}
        checkboxSelection
        disableRowSelectionOnClick
        disableVirtualization={false}
        autoFocus
        slots={{
          toolbar: CustomToolbar,
          columnMenu: CustomColumnMenu,
        }}
        slotProps={{
          toolbar: {
            debouncedSearch,
            handleAddRow,
            handleExport,
            ownerFilter,
            setOwnerFilter,
            ownerList,
            rows,
            columns,
            setFilterModel,
            openFilter,
            setCarrierDialog,
            handleImport,
            downloadSampleExcel,
          },
          columnMenu: {
            openOptionEditor,
            setCarrierDialog,
            setCreditDialog,
            isAdmin,
            setClaimDialog,
            isSuperAdmin,
            setColumnDialog,
            openPermissionDialog,
          },
        }}
        showToolbar
        editMode="cell"
        onCellClick={(params, event) => {
          if (!params.isEditable || params.cellMode === "edit") return;

          event.defaultMuiPrevented = true;

          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          });
        }}
        showCellVerticalBorder
        showColumnVerticalBorder
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
        }}
        processRowUpdate={handleEdit}
        onProcessRowUpdateError={(err) => {
          console.error("FULL ERROR:", err);
          toast.error(err?.response?.data?.message || "Update failed");
        }}
        sortingMode="server"
        onSortModelChange={(model) => {
          if (model.length > 0) {
            setSortField(model[0].field);
            setSortOrder(model[0].sort);
          } else {
            setSortField("id");
            setSortOrder("desc");
          }
        }}
        sx={{
          // borderRadius: 0,
          border: 0,
          width: "100%",
          "& .MuiDataGrid-main": {
            overflow: "hidden",
          },

          "& .MuiDataGrid-toolbar": {
            backgroundColor: "#f5f7fa !important",
            paddingBottom: 3,
            justifyContent: "start",
          },

          "& .MuiDataGrid-cell": {
            outline: "none",
            overflow: "visible",
          },

          "& .row-disabled": {
            backgroundColor: "#0000001e",
            color: "#999",
            cursor: "no-drop",
          },
          "& .column-disabled": {
            backgroundColor: "#e1e1e1",
            color: "#999",
            cursor: "not-allowed",
          },

          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal", // Optional: wrap header text
            textOverflow: "clip",
            textTransform: "capitalize",
            fontSize: 16,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#111827",
            color: "#fff",
            fontSize: "14px",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#de7e30",
          },
          "& .column-permission": {
            backgroundColor: "#ffffff",
            color: "#000",
            cursor: "auto",
          },

          "& .column-permission:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
        isCellEditable={(params) => {
          if (
            params.field === "days_active" ||
            params.field === "days_with_carrier"
          ) {
            return false;
          }

          // Super Admin
          if (isAdmin) {
            return true;
          }

          const allowedUsers = columnRestrictions?.[params.field] || [];

          // Column is restricted
          if (allowedUsers.length > 0) {
            const hasColumnPermission = allowedUsers.includes(user?.username);

            // Super Admin
            if (isAdmin) return true;

            // User not in permission list
            if (!hasColumnPermission) {
              return false;
            }

            // Account user
            if (user?.process === 5) {
              return true; // whole column
            }

            // Agent user
            return params.row.owner_id === user?.username;
          }

          // No restriction on this column
          return params.row.owner_id === user?.username;
        }}
        getCellClassName={(params) => {
          const allowedUsers = columnRestrictions?.[params.field] || [];

          const hasColumnPermission = allowedUsers.includes(user?.username);

          // ACCOUNT USER
          if (user?.process === 5 && hasColumnPermission) {
            return "column-permission";
          }

          // AGENT USER
          if (hasColumnPermission) {
            if (params.row.owner_id === user?.username) {
              return "column-permission";
            }

            return "column-disabled";
          }

          if (!canEditColumn(params.field)) {
            return "column-disabled";
          }

          return "";
        }}
        getRowClassName={(params) => {
          if (isAdmin) return "";

          return params.row.owner_id === user?.username ? "" : "row-disabled";
        }}
        isRowSelectable={(params) => {
          if (isAdmin) return true;

          return params.row.owner_id === user?.username;
        }}
      />
    </Box>
  );
}
